/** AG Grid Enterprise column menu: only the hide/show chooser, no filter/general tabs. */
export const PRICER_COLUMNS_MENU_TABS = ["columnsMenuTab"];

export const PRICER_COLUMN_CHOOSER_HOST_ID = "actions";

/**
 * Grouping, units, row-action host, and other non-user columns. String keys
 * so this module never imports `constants/pricerTable` (that file loads cell
 * renderers). Do not key off `hide` — user-hidden columns must stay listed.
 */
const CHOOSER_EXCLUDED_FIELDS = new Set([
  "",
  "id",
  "is_active",
  "is_locked",
  PRICER_COLUMN_CHOOSER_HOST_ID,
  "summary_reference_id",
  "leg_reference_id",
  "copy_shorthand",
  "copy_shorthand_without_vol",
  "copy_shorthand_without_vol_prem",
  "copy_shorthand_legs",
  "summary_id",
  "level_one_row_type",
  "level_zero_row_type",
  "leg_id",
  "summary_agg",
  "leg_agg",
  "rowColor",
]);

export function isPricerColumnChooserHost(col) {
  return col?.field === PRICER_COLUMN_CHOOSER_HOST_ID;
}

export function isPricerColumnChooserExcluded(col) {
  if (!col) return true;
  if (col.rowGroup) return true;
  if (col.headerCheckboxSelection) return true;
  const field = col.field ?? "";
  const colId = col.colId ?? field;
  if (CHOOSER_EXCLUDED_FIELDS.has(field) || CHOOSER_EXCLUDED_FIELDS.has(colId)) {
    return true;
  }
  return (
    (typeof field === "string" && field.endsWith("_unit")) ||
    (typeof colId === "string" && colId.endsWith("_unit"))
  );
}

export function applyPricerColumnMenuConfig(col) {
  const isHost = isPricerColumnChooserHost(col);
  const excluded = isPricerColumnChooserExcluded(col);
  return {
    ...col,
    menuTabs: isHost ? PRICER_COLUMNS_MENU_TABS : [],
    suppressMenu: !isHost,
    suppressColumnsToolPanel: excluded,
    lockVisible: excluded,
  };
}

export function snapshotPricerColumnLayout(columnState) {
  return (columnState || []).map((item) => ({
    colId: item.colId,
    width: item.width,
    hide: item.hide,
  }));
}

export function shouldPersistPricerColumnLayout(event) {
  if (event?.source === "api") return false;
  if (event?.type === "columnResized" && event.finished === false) return false;
  return true;
}

function isLayoutLockedColDef(colDef) {
  return !!(colDef?.suppressColumnsToolPanel || colDef?.lockVisible);
}

/**
 * Restore widths and user hide via AG Grid column state. Never applyOrder —
 * grouping / Actions stay in def order. Locked columns keep def visibility.
 */
export function applyPricerColumnLayout(columnApi, savedState) {
  if (!columnApi?.applyColumnState || !savedState?.length) return;

  const state = [];
  savedState.forEach((saved) => {
    if (!saved?.colId) return;
    const next = { colId: saved.colId };
    if (saved.width) next.width = saved.width;

    const colDef = columnApi.getColumn?.(saved.colId)?.getColDef?.() ?? {};
    if (typeof saved.hide === "boolean" && !isLayoutLockedColDef(colDef)) {
      next.hide = saved.hide;
    }

    if (next.width || typeof next.hide === "boolean") {
      state.push(next);
    }
  });

  if (!state.length) return;
  columnApi.applyColumnState({ state, applyOrder: false });
}
